import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import KnameBackdrop from './KnameBackdrop';

const GOLD = '#d9b380';
const CREAM = '#f3ede2';
const MUTED = '#9b93a6';

const QUIZ_STARS = [
  { x: '92%', y: '12%', s: 3, o: 0.8 },
  { x: '86%', y: '30%', s: 2, o: 0.5 },
  { x: '15%', y: '92%', s: 3, o: 0.6 },
  { x: '30%', y: '96%', s: 2, o: 0.45 },
  { x: '7%', y: '40%', s: 2, o: 0.5 },
  { x: '45%', y: '82%', s: 2, o: 0.4 },
  { x: '68%', y: '95%', s: 2, o: 0.35 },
];

const GENDERS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'N', label: 'Prefer not to say' },
];

const formatBirthInput = (raw) => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`;
};

const parseBirth = (v) => {
  const match = /^(\d{4})\.(\d{2})\.(\d{2})$/.exec(v);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return { y, m, d, date };
};

const CelestialRings = () => (
  <div
    aria-hidden
    className='pointer-events-none absolute left-[28%] top-[61%] -translate-y-1/2'
  >
    {[550, 420, 300, 180].map((size) => (
      <span
        key={size}
        className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#fff5e6]/5'
        style={{ width: size, height: size }}
      />
    ))}
    <span className='absolute left-1/2 top-1/2 h-[720px] w-px -translate-x-1/2 -translate-y-1/2 bg-[#fff5e6]/[0.03]' />
    <span className='absolute left-1/2 top-1/2 h-px w-[720px] -translate-x-1/2 -translate-y-1/2 bg-[#fff5e6]/[0.03]' />
  </div>
);

const inputClass = (hasError) =>
  `h-13 w-full rounded-xl border bg-[#171019] px-4 py-3.5 text-[16px] text-[${CREAM}] outline-none transition-colors placeholder:text-[#5f5768] ${
    hasError
      ? 'border-[#d97b5f]'
      : 'border-[#4a3f4d] focus:border-[#d9b380] focus:shadow-[0_0_0_3px_rgba(217,179,128,0.15)]'
  }`;

const KnameForm = () => {
  const navigate = useNavigate();
  const EN_ONLY = /^[A-Za-z\s'-]+$/;
  const [familyName, setFamilyName] = useState('');
  const [givenName, setGivenName] = useState('');
  const [birth, setBirth] = useState('');
  const [gender, setGender] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});

  const isBirthValid = (v) => {
    const p = parseBirth(v);
    if (!p || p.date > new Date()) return false;
    const age = (Date.now() - p.date.getTime()) / (365.25 * 24 * 3600 * 1000);
    return age >= 14;
  };

  const getGivenNameError = (v) => {
    if (!v.trim()) return 'Please enter your name.';
    if (!EN_ONLY.test(v))
      return 'Use English letters, spaces, hyphens or apostrophes only.';
    return null;
  };

  const getFamilyNameError = (v) => {
    if (v && !EN_ONLY.test(v))
      return 'Use English letters, spaces, hyphens or apostrophes only.';
    return null;
  };

  const getBirthError = (v) => {
    if (!v || v.length < 10) return 'Please enter a valid date of birth.';
    const p = parseBirth(v);
    if (!p || p.date > new Date()) return 'Please enter a valid date of birth.';
    const age = (Date.now() - p.date.getTime()) / (365.25 * 24 * 3600 * 1000);
    if (age < 14)
      return 'This experience is available to people aged 14 and over.';
    return null;
  };

  const validate = () => {
    const next = {};

    const givenErr = getGivenNameError(givenName);
    if (givenErr) next.givenName = givenErr;

    const familyErr = getFamilyNameError(familyName);
    if (familyErr) next.familyName = familyErr;

    const birthErr = getBirthError(birth);
    if (birthErr) next.birth = birthErr;

    if (!gender) next.gender = 'Please select one.';
    if (!agreed) next.agreed = 'Please agree to continue.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const setFieldError = (field, message) =>
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });

  const stepsDone = [
    familyName.trim() !== '' &&
      EN_ONLY.test(familyName) &&
      givenName.trim() !== '' &&
      EN_ONLY.test(givenName),
    isBirthValid(birth),
    gender !== null,
    agreed,
  ];

  const allDone = stepsDone.every(Boolean);
  const doneCount = stepsDone.filter(Boolean).length;

  const handleSubmit = () => {
    if (!validate()) return;
    const p = parseBirth(birth);
    navigate('/korean-name-generator/form/mood', {
      state: {
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        birthYear: p.y,
        birthMonth: p.m,
        birthDay: p.d,
        gender,
      },
    });
  };

  return (
    <div
      className='relative z-10 mx-auto flex min-h-[100svh] w-full flex-col items-stretch justify-center gap-12
             px-6 py-16 md:flex-row md:items-center md:gap-36 md:px-12 overflow-hidden bg-[#17111d]'
    >
      <KnameBackdrop stars={QUIZ_STARS} glow={false} />
      <CelestialRings />
      <div
        aria-hidden
        className='pointer-events-none absolute left-1/2 top-1/2 h-[480px] w-[1100px] -translate-x-1/2 -translate-y-1/2'
        style={{
          background:
            'radial-gradient(ellipse, rgba(214,162,75,0.1) 0%, rgba(160,120,70,0.15) 10%, transparent 70%)',
        }}
      />

      {/* ── 왼쪽 인트로 ── */}
      <div className='w-full lg:max-w-4xl flex-col lg:pl-[12vw] flex'>
        <p className='text-[12px] lg:text-[13px] uppercase tracking-[0.3em] text-[#f2d29a]'>
          Enter the Celestial Name Atelier
        </p>
        <h1
          className='mt-5 lg:mt-4 text-[36px] lg:text-6xl font-semibold leading-none text-[#fff5e6]'
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Your <span className='text-[#f2d29a]'>Korean name</span>
          <br />
          begins here.
        </h1>
        <p className='mt-6 lg:mt-8 max-w-md text-[15px] leading-relaxed text-[#fff5e694]'>
          Share a few details. We'll match you with a real Korean name that
          suits your generation and chosen style, then craft it into a celestial
          card.
        </p>
      </div>

      {/* ── 오른쪽 폼 카드 ── */}
      <div className='w-full max-w-[560px] rounded-[22px] border border-[#d6a24b47] bg-[#2a1724] p-[28px] md:p-10 lg:mx-[4vw] shadow-3'>
        <p className='text-[11px] uppercase tracking-[0.25em] text-[#f2d29a]'>
          The Details
        </p>
        <h2
          className='mt-3 text-2xl lg:text-3xl text-[#f3ede2]'
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Let's find the name that fits you.
        </h2>

        {/* 스텝 인디케이터 */}
        <div className='mt-5 flex items-center gap-2'>
          {stepsDone.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full transition-all duration-300 ${
                i < doneCount
                  ? 'bg-[#f2d29a] shadow-[0_0_8px_rgba(214,162,75,0.7)]'
                  : 'bg-[#4a3f4d]'
              }`}
            />
          ))}
          <span className='ml-2 h-px flex-1 bg-[#3a3140]' />
        </div>

        {/* 이름 */}
        <p className='mt-8 text-[12px] uppercase tracking-[0.25em] text-[#f2d29a]'>
          Your Current Name
        </p>
        <div className='mt-3 grid lg:grid-cols-2 gap-4'>
          <div>
            <input
              value={familyName}
              placeholder='Family name'
              onChange={(e) => {
                setFamilyName(e.target.value);
                setFieldError('familyName', getFamilyNameError(e.target.value));
              }}
              className={inputClass(!!errors.familyName)}
            />
            <p className='mt-2 text-[11px] text-[#fff5e694]'>
              Your family name will not be changed.
            </p>
            {errors.familyName && (
              <p className='mt-1.5 text-[11px] text-[#e8a87c]'>
                {errors.familyName}
              </p>
            )}
          </div>
          <div>
            <input
              value={givenName}
              onChange={(e) => {
                setGivenName(e.target.value);
                setFieldError('givenName', getGivenNameError(e.target.value));
              }}
              placeholder='Given name'
              className={inputClass(!!errors.givenName)}
            />
            {errors.givenName && (
              <p className='mt-1.5 text-[11px] text-[#e8a87c]'>
                {errors.givenName}
              </p>
            )}
          </div>
        </div>
        <p className='mt-3 text-[11px] text-[#fff5e694]'>
          Use English letters only. We won't guess or compare your original
          pronunciation.
        </p>

        {/* 생년월일 */}
        <label className='mt-7 mb-2 block text-[13px] text-[#fff5e6]'>
          Date of birth
        </label>
        <input
          type='text'
          inputMode='numeric'
          placeholder='YYYY.MM.DD'
          maxLength={10}
          value={birth}
          onChange={(e) => {
            const f = formatBirthInput(e.target.value);
            setBirth(f);
            setFieldError('birth', f.length === 10 ? getBirthError(f) : null);
          }}
          onBlur={() =>
            setFieldError('birth', birth ? getBirthError(birth) : null)
          }
          className={inputClass(!!errors.birth)}
        />
        <p className='mt-1.5 text-[11px] text-[#fff5e694]'>
          Used to match your generation and create your zodiac card.
        </p>
        {errors.birth && (
          <p className='mt-1 text-[11px] text-[#e8a87c]'>{errors.birth}</p>
        )}

        {/* 성별 */}
        <label className='mt-7 mb-1 block text-[13px] text-[#fff5e6]'>
          Gender
        </label>
        <p className='mb-2 text-[11px] text-[#fff5e694]'>
          Used only to guide the naming style.
        </p>
        <div className='grid grid-cols-3 gap-1 rounded-2xl border border-[#4a3f4d] bg-[#171019] p-1'>
          {GENDERS.map((g) => (
            <button
              key={g.value}
              type='button'
              onClick={() => {
                setGender(g.value);
                setFieldError('gender', null);
              }}
              className={`rounded-xl px-3 py-1 lg:py-3 text-[15px] transition-all ${
                gender === g.value
                  ? 'bg-[#d6a24b] font-semibold text-[#17111f] shadow-[0_4px_12px_-4px_rgba(214,162,75,0.5)]'
                  : 'text-[#fff5e6] hover:bg-[#241a26]'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
        {errors.gender && (
          <p className='mt-1.5 text-[11px] text-[#e8a87c]'>{errors.gender}</p>
        )}

        {/* 동의 */}
        <label className='mt-8 flex cursor-pointer items-start gap-3 text-[12.5px] leading-relaxed text-[#fff5e694]'>
          <input
            type='checkbox'
            checked={agreed}
            onChange={(e) => {
              setAgreed(e.target.checked);
              setFieldError(
                'agreed',
                e.target.checked
                  ? null
                  : errors.agreed
                    ? 'Please agree to continue.'
                    : null,
              );
            }}
            className='mt-0.5 h-[20px] w-[20px] shrink-0 cursor-pointer accent-[#d6a24b]'
          />
          <span>
            I agree to the collection and use of my family name, given name,
            date of birth and selected gender to generate my Korean name and
            card.{' '}
            <a
              href='https://southern-basketball-646.notion.site/Koready-K-name-Generator-Policy-Hub-3b1392396e9e803bb96cd54ff765ff82?source=copy_link'
              target='_blank'
              rel='noopener noreferrer'
              className='underline'
            >
              Privacy Policy
            </a>
          </span>
        </label>
        <p className='mt-2 pl-[30px] text-[13px] text-[#8a8296]'>
          Your original inputs are deleted immediately after your result is
          generated.
        </p>
        {errors.agreed && (
          <p className='mt-1 pl-[30px] text-[13px] text-[#d97b5f]'>
            {errors.agreed}
          </p>
        )}

        {/* 제출 */}
        <button
          type='button'
          onClick={handleSubmit}
          disabled={!allDone}
          className='mt-8 h-14 w-full rounded-full bg-[#d6a24b] text-[17px] font-semibold text-[#17111d] shadow-[0_10px_30px_-12px_rgba(214,162,75,0.55)] transition-all hover:-translate-y-0.5 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:brightness-100'
        >
          Reveal My Korean Name
        </button>

        <div className='mt-5 text-center'>
          <button
            type='button'
            onClick={() => navigate('/korean-name-generator')}
            className='text-[12px] text-[#fff5e694] underline'
          >
            Back to entrance
          </button>
        </div>
      </div>
      <footer className='absolute right-[39%] bottom-6 z-10 text-xs text-[#fff5e694]'>
        © 2026 Koready
      </footer>
    </div>
  );
};

export default KnameForm;
